// FunctionList.tsx - Updated: row-click + 3-dot menu both active
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../EnterpriseTable.css'; 
import '../companyManagement/CompanyListOverrides.css';
import '../companyManagement/CompanyHelpPanel.css';
import '../companyManagement/ViewDetailsModal.css';
import { FunctionsApiService } from './apiService';
import { Function, Company, FunctionsManagementProps, FunctionApprover, FunctionReviewer } from './types';
import { FunctionDeleteConfirmationModal } from './FunctionDeleteConfirmationModal';

interface FunctionListProps extends FunctionsManagementProps {
  functions: Function[];
  selectedCompany: Company | null;
  isLoading: boolean;
  onEdit: (func: Function) => void;
  onCreateNew: () => void;
  onRefresh: () => void;
  onBackToCompanySelection: () => void;
  hideBackButton?: boolean;
  onManageUsers?: () => void;
}

interface DropdownState {
  isOpen: boolean;
  functionId: string | null;
  position: { top: number; left: number; openUpward: boolean } | null;
}

// Component to display user list with email tooltips
const UserList: React.FC<{ 
  users: (FunctionApprover | FunctionReviewer)[], 
  type: 'Approvers' | 'Reviewers',
  maxVisible?: number 
}> = ({ users, type, maxVisible = 2 }) => {
  if (!users || users.length === 0) {
    return <span className="no-users-text">No {type.toLowerCase()}</span>;
  }

  const visibleUsers = users.slice(0, maxVisible);
  const remainingCount = users.length - maxVisible;

  return (
    <div className="user-list-container">
      {visibleUsers.map((user, index) => (
        <span 
          key={user.userId} 
          className="user-badge-mini"
          title={`${user.userName} (${user.userEmail})`}
        >
          {user.userName}
        </span>
      ))}
      {remainingCount > 0 && (
        <span 
          className="remaining-users-mini"
          title={users.slice(maxVisible).map(u => `${u.userName} (${u.userEmail})`).join('\n')}
        >
          +{remainingCount} more
        </span>
      )}
    </div>
  );
};

// Updated Function Detail Modal - with action buttons in footer
const FunctionDetailModal: React.FC<{
  func: Function;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (func: Function) => void;
  onDelete: (func: Function) => void;
  onManageUsers?: () => void;
}> = ({ func, isOpen, onClose, onEdit, onDelete, onManageUsers }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset delete confirm when modal opens/closes
  useEffect(() => {
    if (!isOpen) setShowDeleteConfirm(false);
  }, [isOpen]);

  // Prevent scroll propagation to background
  const handleWheel = useCallback((e: React.WheelEvent) => {
    const modalBody = modalBodyRef.current;
    if (!modalBody) return;

    const { scrollTop, scrollHeight, clientHeight } = modalBody;
    const isScrollingDown = e.deltaY > 0;
    const isScrollingUp = e.deltaY < 0;

    if (isScrollingDown && scrollTop + clientHeight >= scrollHeight) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (isScrollingUp && scrollTop <= 0) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.stopPropagation();
  }, []);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleModalContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleEditClick = () => {
    onClose();
    onEdit(func);
  };

  const handleDeleteClick = () => {
    onClose();
    onDelete(func);
  };

  const handleManageUsersClick = () => {
    if (onManageUsers) {
      onClose();
      onManageUsers();
    }
  };

  return (
    <div 
      className="modal-overlay view-details-overlay"
      onClick={handleOverlayClick}
      onWheel={(e) => e.preventDefault()}
    >
      <div 
        ref={modalRef}
        className="view-details-modal"
        onClick={handleModalContentClick}
        onWheel={handleWheel}
      >
        <div className="view-details-header">
          <div className="view-details-header-left">
            <h2 className="view-details-title">{func.functionName}</h2>
          </div>
          <button 
            type="button" 
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div 
          ref={modalBodyRef}
          className="view-details-body"
        >
          <div className="view-details-section">
            <h3 className="section-title">Basic Info</h3>
          </div>

          <div className="view-details-grid">
            <div className="view-details-left">
              <div className="detail-field">
                <label className="detail-label">Division Name</label>
                <div className="detail-value company-name">{func.functionName}</div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Company</label>
                <div className="detail-value">{func.companyName}</div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Function ID</label>
                <div className="detail-value">#{func.functionId}</div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Created</label>
                <div className="detail-value">
                  {func.createdAt ? new Date(func.createdAt).toLocaleDateString() : 'N/A'}
                </div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Last Updated</label>
                <div className="detail-value">
                  {func.updatedAt ? new Date(func.updatedAt).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            </div>

            <div className="view-details-right">
              {/* Approvers Section */}
              <div className="detail-field">
                <label className="detail-label">Approvers ({func.approvers?.length || 0})</label>
                <div className="detail-value regions">
                  {func.approvers && func.approvers.length > 0 ? (
                    <div className="regions-list">
                      {func.approvers.map((approver) => (
                        <div key={approver.userId} className="region-item-display">
                          <div className="region-main-info">
                            <span className="region-name">{approver.userName}</span>
                          </div>
                          <div className="region-meta-info">
                            <span className="region-file-id">
                              {approver.userEmail}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    'No approvers assigned'
                  )}
                </div>
              </div>

              {/* Reviewers Section */}
              <div className="detail-field">
                <label className="detail-label">Reviewers ({func.reviewers?.length || 0})</label>
                <div className="detail-value regions">
                  {func.reviewers && func.reviewers.length > 0 ? (
                    <div className="regions-list">
                      {func.reviewers.map((reviewer) => (
                        <div key={reviewer.userId} className="region-item-display">
                          <div className="region-main-info">
                            <span className="region-name">{reviewer.userName}</span>
                          </div>
                          <div className="region-meta-info">
                            <span className="region-file-id">
                              {reviewer.userEmail}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    'No reviewers assigned'
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Description Field - Full Width */}
          <div className="view-details-section">
            <div className="detail-field full-width">
              <label className="detail-label">Description</label>
              <div className="detail-value description">
                {func.functionDescription || 'No description provided'}
              </div>
            </div>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="view-details-footer">
          <div className="vdm-actions-left">
            {!showDeleteConfirm && (
              <button 
                type="button" 
                className="vdm-action-btn delete"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Delete
              </button>
            )}
            {showDeleteConfirm && (
              <div className="vdm-delete-confirm">
                <span className="vdm-delete-confirm-text">Delete this division?</span>
                <button 
                  type="button" 
                  className="vdm-action-btn delete-confirm"
                  onClick={handleDeleteClick}
                >
                  Yes, Delete
                </button>
                <button 
                  type="button" 
                  className="vdm-action-btn cancel-delete"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div className="vdm-actions-right">
            {onManageUsers && (
              <button 
                type="button" 
                className="vdm-action-btn secondary"
                onClick={handleManageUsersClick}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                Users
              </button>
            )}
            <button 
              type="button" 
              className="vdm-action-btn primary"
              onClick={handleEditClick}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Edit Division
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const FunctionList: React.FC<FunctionListProps> = ({
  functions,
  selectedCompany,
  isLoading,
  getAccessToken,
  onStatusUpdate,
  onEdit,
  onCreateNew,
  onRefresh,
  onBackToCompanySelection,
  hideBackButton = false,
  onManageUsers
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [helpVisible, setHelpVisible] = useState(() => {
    try {
      const stored = localStorage.getItem('gateqx-help-visible-divisions');
      return stored !== null ? stored === 'true' : true;
    } catch { return true; }
  });
  const [showActionHint, setShowActionHint] = useState(false);
  const [selectedFunction, setSelectedFunction] = useState<Function | null>(null);
  const [apiService] = useState(() => new FunctionsApiService(getAccessToken));
  const [dropdown, setDropdown] = useState<DropdownState>({ isOpen: false, functionId: null, position: null });
  
  // Delete Confirmation Modal State
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    function: null as Function | null
  });
  
  // Refs for trigger buttons
  const dropdownButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const portalDropdownRef = useRef<HTMLDivElement>(null);

  // Persist help guide visibility
  useEffect(() => {
    try { localStorage.setItem('gateqx-help-visible-divisions', String(helpVisible)); } catch {}
  }, [helpVisible]);

  // Filter functions based on search term
  const filteredFunctions = useMemo(() => {
    if (!searchTerm.trim()) {
      return functions;
    }

    const term = searchTerm.toLowerCase().trim();
    
    return functions.filter(func => 
      func.functionName.toLowerCase().includes(term) ||
      func.functionDescription.toLowerCase().includes(term) ||
      func.approvers?.some(a => 
        a.userName.toLowerCase().includes(term) || 
        a.userEmail.toLowerCase().includes(term)
      ) ||
      func.reviewers?.some(r => 
        r.userName.toLowerCase().includes(term) || 
        r.userEmail.toLowerCase().includes(term)
      )
    );
  }, [functions, searchTerm]);

  // Close dropdown on scroll or resize
  useEffect(() => {
    if (!dropdown.isOpen) return;
    
    const closeDropdownFn = () => {
      setDropdown({ isOpen: false, functionId: null, position: null });
    };

    window.addEventListener('scroll', closeDropdownFn, true);
    window.addEventListener('resize', closeDropdownFn);
    return () => {
      window.removeEventListener('scroll', closeDropdownFn, true);
      window.removeEventListener('resize', closeDropdownFn);
    };
  }, [dropdown.isOpen]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdown.isOpen || !dropdown.functionId) return;

      const buttonElement = dropdownButtonRefs.current.get(dropdown.functionId);
      const portalElement = portalDropdownRef.current;
      
      if (buttonElement && !buttonElement.contains(event.target as Node) &&
          (!portalElement || !portalElement.contains(event.target as Node))) {
        setDropdown({ isOpen: false, functionId: null, position: null });
      }
    };

    if (dropdown.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdown.isOpen, dropdown.functionId]);

  // Auto-dismiss action hint after 6 seconds or on first dropdown open
  useEffect(() => {
    if (!showActionHint) return;
    const timer = setTimeout(() => setShowActionHint(false), 6000);
    return () => clearTimeout(timer);
  }, [showActionHint]);

  useEffect(() => {
    if (dropdown.isOpen) setShowActionHint(false);
  }, [dropdown.isOpen]);

  // Toggle dropdown - calculates position for portal rendering
  const toggleDropdown = (functionId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click

    if (dropdown.isOpen && dropdown.functionId === functionId) {
      setDropdown({ isOpen: false, functionId: null, position: null });
    } else {
      const buttonEl = dropdownButtonRefs.current.get(functionId);
      if (buttonEl) {
        const rect = buttonEl.getBoundingClientRect();
        const menuHeight = 160;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpward = spaceBelow < menuHeight;

        setDropdown({
          isOpen: true,
          functionId,
          position: {
            top: openUpward ? rect.top : rect.bottom + 4,
            left: rect.right,
            openUpward
          }
        });
      }
    }
  };

  // Row click opens detail modal
  const handleRowClick = (func: Function) => {
    setSelectedFunction(func);
  };

  // Handle delete function
  const handleDelete = (func: Function) => {
    setDropdown({ isOpen: false, functionId: null, position: null });
    setDeleteModal({
      isOpen: true,
      function: func
    });
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!deleteModal.function) return;

    const result = await apiService.deleteFunction(deleteModal.function);
    
    setDeleteModal({ isOpen: false, function: null });
    
    if (result.success) {
      onStatusUpdate({
        type: 'success',
        message: `The division "${deleteModal.function.functionName}" has been successfully deleted from the system.`
      });
      onRefresh();
    } else {
      onStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to delete function'
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, function: null });
  };

  // Handle view details from dropdown
  const handleViewDetails = (func: Function) => {
    setDropdown({ isOpen: false, functionId: null, position: null });
    setSelectedFunction(func);
  };

  // Handle edit from dropdown or modal
  const handleEdit = (func: Function) => {
    setDropdown({ isOpen: false, functionId: null, position: null });
    onEdit(func);
  };

  // Handle manage users from dropdown
  const handleManageUsers = () => {
    setDropdown({ isOpen: false, functionId: null, position: null });
    if (onManageUsers) onManageUsers();
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  if (isLoading) {
    return (
      <div className="loading">
        <span className="spinner"></span>
        Loading Divisions...
      </div>
    );
  }

  if (functions.length === 0) {
    return (
      <div className="company-management">
        {!hideBackButton && (
          <div className="breadcrumb-section">
            <button 
              onClick={onBackToCompanySelection}
              className="breadcrumb-link"
            >
              ← Companies
            </button>
          </div>
        )}
        <div className="no-companies">
          <div className="no-data-icon">⚙️</div>
          <h3>No Divisions Found</h3>
          <p>Start by adding the first function/role for {selectedCompany?.companyName}.</p>
          <button onClick={onCreateNew} className="create-first-btn">
            Add First Function
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="company-management">
      {!hideBackButton && (
        <div className="breadcrumb-section">
          <button 
            onClick={onBackToCompanySelection}
            className="breadcrumb-link"
          >
            &larr; Companies
          </button>
          <h3 className="page-title">Divisions &mdash; {selectedCompany?.companyName}</h3>
        </div>
      )}
      
      <div className={`cm-layout ${!helpVisible ? 'help-collapsed' : ''}`}>
        <div className="cm-main">
      
      <div className="companies-container">
        {/* Toolbar */}
        <div className="table-toolbar">
          <div className="toolbar-left">
            <span className="record-count">{functions.length} divisions</span>
          </div>
          <div className="toolbar-right">
            <div className="search-field">
              <svg className="search-icon-svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search divisions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button onClick={clearSearch} className="clear-search-btn" title="Clear search">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <button onClick={onCreateNew} className="create-company-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Add Division</span>
            </button>
          </div>
        </div>

        {searchTerm && (
          <div className="search-results-bar">
            Showing {filteredFunctions.length} of {functions.length} results
            <button onClick={clearSearch} className="clear-filter-btn">Clear filter</button>
          </div>
        )}

        <div className="companies-table-container">
          <table className="minimalist-table">
            <thead>
              <tr>
                <th style={{ width: '30%' }}>DIVISION NAME</th>
                <th style={{ width: '30%' }}>APPROVERS</th>
                <th style={{ width: '30%' }}>REVIEWERS</th>
                <th style={{ width: '10%' }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredFunctions.length === 0 ? (
                <tr>
                  <td colSpan={4} className="no-results">
                    <div className="no-results-content">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <p>No divisions found matching your search</p>
                      <button onClick={clearSearch} className="clear-search-link">Clear search</button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredFunctions.map((func, rowIndex) => (
                  <tr
                    key={func.functionId}
                    className="clickable-row"
                    onClick={() => handleRowClick(func)}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(func); }}
                  >
                    <td className="org-name">
                      <div className="org-name-text">{func.functionName}</div>
                    </td>
                    <td className="function-users">
                      <UserList users={func.approvers || []} type="Approvers" maxVisible={2} />
                    </td>
                    <td className="function-users">
                      <UserList users={func.reviewers || []} type="Reviewers" maxVisible={2} />
                    </td>
                    <td className="td-actions" onClick={(e) => e.stopPropagation()}>
                      <div className="functions-action-dropdown">
                        {rowIndex === 0 && showActionHint && (
                          <div className="action-hint-tooltip" onClick={() => setShowActionHint(false)}>
                            <span>Click to view, edit, or delete this division</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </div>
                        )}
                        <button
                          ref={(el) => {
                            if (el) {
                              dropdownButtonRefs.current.set(func.functionId.toString(), el);
                            } else {
                              dropdownButtonRefs.current.delete(func.functionId.toString());
                            }
                          }}
                          onClick={(e) => toggleDropdown(func.functionId.toString(), e)}
                          className={`functions-action-trigger ${rowIndex === 0 && showActionHint ? 'hint-pulse' : ''}`}
                          title="More actions"
                          type="button"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="2" />
                            <circle cx="12" cy="12" r="2" />
                            <circle cx="12" cy="19" r="2" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
        </div>

        {/* Right: Help Guide — animated collapse */}
        <div
          className="cm-help-right"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            width: helpVisible ? 310 : 44,
            padding: '28px 0 32px 0',
            transition: 'width 0.3s ease',
            overflow: 'hidden',
          }}
        >
          <div
            className="cm-help-content"
            style={{
              flex: helpVisible ? 1 : '0 0 0px',
              opacity: helpVisible ? 1 : 0,
              overflow: 'hidden',
              transition: 'opacity 0.25s ease, flex 0.3s ease',
              pointerEvents: helpVisible ? 'auto' : 'none',
              minWidth: 0,
              padding: helpVisible ? '0 16px 0 28px' : '0',
            }}
          >
            <div className="cm-help-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <p className="cm-help-title">Division Management</p>
            <p className="cm-help-text">
              Divisions define the approval workflow for gate pass requests within this company.
            </p>
            <p className="cm-help-subtitle">Row &amp; Quick Actions</p>
            <p className="cm-help-text">
              Click any row to view full details with action buttons inside. Or use the three-dot menu for quick access to edit, manage users, or delete.
            </p>
            <p className="cm-help-subtitle">Approvers</p>
            <p className="cm-help-text">
              Each division must have at least one approver. Approvers receive gate pass requests and can approve or reject them.
            </p>
            <p className="cm-help-subtitle">Reviewers</p>
            <p className="cm-help-text">
              Reviewers are optional. When assigned, they review requests before approvers see them — adding an extra validation step.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setHelpVisible(!helpVisible)}
            aria-label={helpVisible ? 'Hide Guide' : 'Show Guide'}
            title={helpVisible ? 'Hide Guide' : 'Show Guide'}
            style={helpVisible ? {
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              background: '#f5f5f5',
              border: '1px solid #e0e0e0',
              borderRight: 'none',
              borderRadius: '6px 0 0 6px',
              padding: '12px 7px',
              fontSize: 12,
              fontWeight: 600,
              color: '#374151',
              cursor: 'pointer',
              letterSpacing: '0.02em',
              flexShrink: 0,
              alignSelf: 'flex-start',
              marginTop: 36,
              zIndex: 2,
              transition: 'background 0.15s ease',
            } : {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: '50%',
              border: '1.5px solid #d4d4d4',
              background: '#ffffff',
              cursor: 'pointer',
              flexShrink: 0,
              margin: '6px auto 0 auto',
              padding: 0,
              transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (helpVisible) {
                e.currentTarget.style.background = '#ebebeb';
              } else {
                e.currentTarget.style.borderColor = '#F3694A';
                e.currentTarget.style.background = '#fef7f5';
              }
            }}
            onMouseLeave={(e) => {
              if (helpVisible) {
                e.currentTarget.style.background = '#f5f5f5';
              } else {
                e.currentTarget.style.borderColor = '#d4d4d4';
                e.currentTarget.style.background = '#ffffff';
              }
            }}
          >
            {helpVisible ? (
              <>
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ flexShrink: 0, transform: 'rotate(90deg)' }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M15 9l-6 3 6 3" />
                </svg>
                <span>Hide Guide</span>
              </>
            ) : (
              <svg
                width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="#848d9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Portal-rendered Dropdown Menu */}
      {dropdown.isOpen && dropdown.functionId && dropdown.position && createPortal(
        <div
          ref={portalDropdownRef}
          className="portal-dropdown-menu"
          style={{
            position: 'fixed',
            top: dropdown.position.openUpward ? 'auto' : `${dropdown.position.top}px`,
            bottom: dropdown.position.openUpward ? `${window.innerHeight - dropdown.position.top + 4}px` : 'auto',
            left: `${dropdown.position.left}px`,
            transform: 'translateX(-100%)',
            zIndex: 99999,
            minWidth: '160px',
            maxWidth: '200px',
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)',
            padding: '4px',
            userSelect: 'none',
          }}
        >
          {(() => {
            const activeFunc = filteredFunctions.find(f => f.functionId.toString() === dropdown.functionId);
            if (!activeFunc) return null;
            return (
              <>
                <button onClick={() => handleViewDetails(activeFunc)} className="functions-dropdown-item" type="button">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  View Details
                </button>
                <button onClick={() => handleEdit(activeFunc)} className="functions-dropdown-item" type="button">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit
                </button>
                {onManageUsers && (
                  <button onClick={() => handleManageUsers()} className="functions-dropdown-item" type="button">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    Manage Users
                  </button>
                )}
                <div className="dropdown-divider"></div>
                <button onClick={() => handleDelete(activeFunc)} className="functions-dropdown-item delete" type="button">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  Delete
                </button>
              </>
            );
          })()}
        </div>,
        document.body
      )}

      {/* Function Detail Modal with action buttons */}
      {selectedFunction && (
        <FunctionDetailModal
          func={selectedFunction}
          isOpen={true}
          onClose={() => setSelectedFunction(null)}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onManageUsers={onManageUsers}
        />
      )}

      {/* Delete Confirmation Modal */}
      <FunctionDeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        functionName={deleteModal.function?.functionName || ''}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};